import { useCallback } from "react";
import { useFormContext } from "react-hook-form";

import FormFieldRowWrapper from "ds/components/Form/Field/RowWrapper";
import FormField from "ds/components/Form/Field";
import { TooltipModalTitle } from "ds/components/TooltipModal/Title";
import TooltipModalBody from "ds/components/TooltipModal/Body";
import Input from "ds/components/Input";
import FormArrayField from "ds/components/Form/ArrayField";
import { getDocsUrl } from "utils/getDocsUrl";
import ReadMoreDocsLink from "components/ReadMoreDocsLink";

import { createGitSparseCheckoutPathValidator } from "./helpers";

type FormFieldValues = Record<"gitSparseCheckoutPaths", Record<"value", string>[]>;

const EMPTY_VALUE = { value: "" };

const FormFieldGitSparseCheckoutPaths = () => {
  const {
    formState: { errors },
    getValues,
    register,
  } = useFormContext<FormFieldValues>();

  const handleOnKeyDown = useCallback(
    (callback?: () => void) => (e: React.KeyboardEvent) => {
      if (callback && e.key === "Enter") {
        callback();
      }
    },
    []
  );

  const getGitSparseCheckoutPathValidator = useCallback(
    (index: number) => createGitSparseCheckoutPathValidator(getValues, index),
    [getValues]
  );

  return (
    <FormArrayField<FormFieldValues, "gitSparseCheckoutPaths">
      name="gitSparseCheckoutPaths"
      addButtonLabel="Add another path"
      emptyValue={EMPTY_VALUE}
    >
      {({ field, index, addEmptyItem, suffix }) => (
        <FormField
          key={field.id}
          error={errors?.gitSparseCheckoutPaths?.[index]?.value?.message}
          label={index === 0 ? "Git sparse checkout paths" : undefined}
          isOptional
          tooltipInfoVariant="modal"
          tooltipInfo={
            <>
              <TooltipModalTitle>Git sparse checkout paths</TooltipModalTitle>
              <TooltipModalBody align="start">
                Git sparse checkout allows you to check out only a subset of files from a
                repository, rather than the entire working directory. This is particularly useful
                for large repositories where you only need to work with specific directories or
                files.
                <ReadMoreDocsLink
                  docsUrl={getDocsUrl("/concepts/stack/stack-settings#project-globs")}
                />
              </TooltipModalBody>
            </>
          }
          fullWidth
          noMargin
        >
          {({ ariaInputProps }) => (
            <FormFieldRowWrapper>
              <Input
                onKeyDown={handleOnKeyDown(addEmptyItem)}
                placeholder="Type path here"
                error={!!errors?.gitSparseCheckoutPaths?.[index]?.value?.message}
                {...register(`gitSparseCheckoutPaths.${index}.value`, {
                  validate: {
                    gitSparseCheckoutPath: getGitSparseCheckoutPathValidator(index),
                  },
                })}
                {...ariaInputProps}
              />
              {suffix}
            </FormFieldRowWrapper>
          )}
        </FormField>
      )}
    </FormArrayField>
  );
};

export default FormFieldGitSparseCheckoutPaths;
